import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  addWeekdayCheckInRestriction,
  addWeekdayCheckOutRestriction,
  getWeekdayCheckInRulesAsync,
  getWeekdayCheckOutRulesAsync,
  removeWeekdayCheckInRestriction,
  removeWeekdayCheckOutRestriction,
  selectWeekdayCheckInRuleItems,
  selectWeekdayCheckInRuleItemsLoadingStatus,
  selectWeekdayCheckOutRuleItems,
  selectWeekdayCheckOutRuleItemsLoadingStatus,
  updateActiveForWeekdayCheckInRestrictionData,
  updateActiveForWeekdayCheckOutRestrictionData,
  updateCopyableForWeekdayCheckInRestrictionData,
  updateCopyableForWeekdayCheckOutRestrictionData,
  updateSeasonRateWeekdayCheckInAndCheckOutRulesAsync,
} from '../seasonRateWeekdayRulesSlice';
import WeekdayCheckInCheckOutRestrictionsForm from './WeekdayCheckInCheckOutRestrictionsForm';
import {
  WeekdayCheckInRuleItem,
  WeekdayCheckOutRuleItem,
} from '../../../common/types/rates.types';

function SeasonRateWeekdayCheckInCheckOutRestrictions({
  seasonRateId,
  hasWriteAccess,
}: {
  seasonRateId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const weekdayCheckInRuleItems = useAppSelector(selectWeekdayCheckInRuleItems);
  const weekdayCheckOutRuleItems = useAppSelector(
    selectWeekdayCheckOutRuleItems,
  );
  const weekdayCheckInRuleItemsStatus = useAppSelector(
    selectWeekdayCheckInRuleItemsLoadingStatus,
  );
  const weekdayCheckOutRuleItemsStatus = useAppSelector(
    selectWeekdayCheckOutRuleItemsLoadingStatus,
  );

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    dispatch(getWeekdayCheckInRulesAsync(seasonRateId));
    dispatch(getWeekdayCheckOutRulesAsync(seasonRateId));
  }, [seasonRateId, dispatch]);

  const handleWeekdayCheckInRuleChange = (event, weekdayInt) => {
    const { checked } = event.target;
    if (checked) {
      dispatch(removeWeekdayCheckInRestriction(weekdayInt));
    } else {
      const newWeekdayCheckInRestriction = {} as WeekdayCheckInRuleItem;
      newWeekdayCheckInRestriction.weekday = weekdayInt;
      newWeekdayCheckInRestriction.seasonRateId = seasonRateId;
      newWeekdayCheckInRestriction.isCopyable = true;
      newWeekdayCheckInRestriction.isActive = true;

      dispatch(addWeekdayCheckInRestriction(newWeekdayCheckInRestriction));
    }
  };

  const handleWeekdayCheckOutRuleChange = (event, weekdayInt) => {
    const { checked } = event.target;
    if (checked) {
      dispatch(removeWeekdayCheckOutRestriction(weekdayInt));
    } else {
      const newWeekdayCheckOutRestriction = {} as WeekdayCheckOutRuleItem;
      newWeekdayCheckOutRestriction.weekday = weekdayInt;
      newWeekdayCheckOutRestriction.seasonRateId = seasonRateId;
      newWeekdayCheckOutRestriction.isCopyable = true;
      newWeekdayCheckOutRestriction.isActive = true;

      dispatch(addWeekdayCheckOutRestriction(newWeekdayCheckOutRestriction));
    }
  };

  const handleCopyableWeekdayCheckInChange = event => {
    const { checked } = event.target;
    dispatch(
      updateCopyableForWeekdayCheckInRestrictionData({ copyable: checked }),
    );
  };

  const handleCopyableWeekdayCheckOutChange = event => {
    const { checked } = event.target;
    dispatch(
      updateCopyableForWeekdayCheckOutRestrictionData({ copyable: checked }),
    );
  };

  const handleActiveWeekdayCheckInChange = event => {
    const { checked } = event.target;
    dispatch(updateActiveForWeekdayCheckInRestrictionData({ active: checked }));
  };

  const handleActiveWeekdayCheckOutChange = event => {
    const { checked } = event.target;
    dispatch(
      updateActiveForWeekdayCheckOutRestrictionData({ active: checked }),
    );
  };

  const onSubmit = () => {
    const rulesData = {
      seasonRateId,
      weekdayCheckInRules: weekdayCheckInRuleItems,
      weekdayCheckOutRules: weekdayCheckOutRuleItems,
    };

    dispatch(updateSeasonRateWeekdayCheckInAndCheckOutRulesAsync(rulesData));
    setSubmitted(true);
  };

  return (
    <WeekdayCheckInCheckOutRestrictionsForm
      checkInLoadingStatus={weekdayCheckInRuleItemsStatus}
      checkOutLoadingStatus={weekdayCheckOutRuleItemsStatus}
      handleCheckInInputChange={handleWeekdayCheckInRuleChange}
      handleCheckOutInputChange={handleWeekdayCheckOutRuleChange}
      handleActiveWeekdayCheckInChange={handleActiveWeekdayCheckInChange}
      handleCopyableWeekdayCheckInChange={handleCopyableWeekdayCheckInChange}
      handleActiveWeekdayCheckOutChange={handleActiveWeekdayCheckOutChange}
      handleCopyableWeekdayCheckOutChange={handleCopyableWeekdayCheckOutChange}
      onSubmit={onSubmit}
      submitted={submitted}
      weekdayCheckInRules={weekdayCheckInRuleItems}
      weekdayCheckOutRules={weekdayCheckOutRuleItems}
      hasWriteAccess={hasWriteAccess}
    />
  );
}

export default SeasonRateWeekdayCheckInCheckOutRestrictions;
